import type { AnnotationFrameDraft, AnnotationFrameLocator, AnnotationSetDraft } from "./types";

// Absorb media-clock and decoder rounding without conflating adjacent video frames.
const CAPTURE_TIME_EPSILON_SECONDS = 1e-3;
const TIMELINE_DISPLAY_FRACTION_SCALE = 10;

function timelineDisplayTick(timestampSeconds: number): number | null {
  if (!Number.isFinite(timestampSeconds)) return null;
  return Math.round(Math.max(0, timestampSeconds) * TIMELINE_DISPLAY_FRACTION_SCALE);
}

/** Finds the first matching frame in an ascending timestamp order. */
export function findCapturedFrameAtTime<TFrame extends AnnotationFrameLocator>(
  sortedFrames: readonly TFrame[],
  timestampSeconds: number,
): TFrame | undefined {
  if (!Number.isFinite(timestampSeconds)) return undefined;

  const lowerBound = timestampSeconds - CAPTURE_TIME_EPSILON_SECONDS;
  let low = 0;
  let high = sortedFrames.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    const frame = sortedFrames[middle];
    if (frame !== undefined && frame.timestampSeconds < lowerBound) low = middle + 1;
    else high = middle;
  }

  for (let index = low; index < sortedFrames.length; index += 1) {
    const frame = sortedFrames[index];
    if (
      frame === undefined ||
      frame.timestampSeconds > timestampSeconds + CAPTURE_TIME_EPSILON_SECONDS
    ) {
      break;
    }
    if (Math.abs(frame.timestampSeconds - timestampSeconds) <= CAPTURE_TIME_EPSILON_SECONDS) {
      return frame;
    }
  }
  return undefined;
}

/** Finds the nearest frame rendered at the same tenth-second timeline readout. */
export function findCapturedFrameAtTimelineTime<TFrame extends AnnotationFrameLocator>(
  sortedFrames: readonly TFrame[],
  timestampSeconds: number,
): TFrame | undefined {
  const targetTick = timelineDisplayTick(timestampSeconds);
  if (targetTick === null) return undefined;

  let low = 0;
  let high = sortedFrames.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    const frame = sortedFrames[middle];
    const frameTick = frame ? timelineDisplayTick(frame.timestampSeconds) : null;
    if (frameTick !== null && frameTick < targetTick) low = middle + 1;
    else high = middle;
  }

  let nearestFrame: TFrame | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let index = low; index < sortedFrames.length; index += 1) {
    const frame = sortedFrames[index];
    if (!frame || timelineDisplayTick(frame.timestampSeconds) !== targetTick) break;
    const distance = Math.abs(frame.timestampSeconds - timestampSeconds);
    if (distance < nearestDistance) {
      nearestFrame = frame;
      nearestDistance = distance;
    }
  }
  return nearestFrame;
}

export function findCapturedFrameAt(
  draft: AnnotationSetDraft | null,
  sequenceId: string | null,
  streamId: string | null,
  timestampSeconds: number,
): AnnotationFrameDraft | undefined {
  if (!draft || sequenceId === null || streamId === null || !Number.isFinite(timestampSeconds)) {
    return undefined;
  }

  for (const frameId of draft.orderedFrameIds) {
    const frame = draft.frames[frameId];
    if (
      frame &&
      frame.sequenceId === sequenceId &&
      frame.streamId === streamId &&
      Math.abs(frame.timestampSeconds - timestampSeconds) <= CAPTURE_TIME_EPSILON_SECONDS
    ) {
      return frame;
    }
  }
  return undefined;
}
